import type { AddressViewModel } from '@generated';

export const formatAddress = (address: AddressViewModel | null | undefined, noAddressText = '-') => {
	if (!address) {
		return noAddressText;
	}
	return `${address.addressOne}, ${address.city}, ${address.state} ${address.zipCode}`;
};

export const isEmptyObject = (objectToTest: unknown) => {
	return JSON.stringify(objectToTest) === '{}';
};

export const formatPhoneNumber = (phoneNumber: string | null | undefined, noPhoneNumberText = '-') =>
	phoneNumber ? phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') : noPhoneNumberText;

export const prettySocialSecurityNumber = (ssn: string) => {
	return `${ssn.substring(0, 3)}-${ssn.substring(3, 5)}-${ssn.substring(5)}`;
};

export const formatStringToTitleCase = (string: string) => {
	return string.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
	});
};

export const loadScript = (src: string, position: HTMLElement | null, id: string) => {
	if (!position) {
		return;
	}
	const script = document.createElement('script');
	script.setAttribute('async', '');
	script.setAttribute('id', id);
	script.src = src;
	position.appendChild(script);
};

export const makePhoneCall = (phoneNumber: string) => {
	window.open(`tel:${phoneNumber}`, '_blank');
};

export const sortAlphabetically = (array: Record<string, string>[], property: string) => {
	return array.sort((a, b) => {
		let aProp = a[property];
		let bProp = b[property];
		if (!aProp && !bProp) return 0;
		if (!aProp) return -1;
		if (!bProp) return 1;
		aProp = aProp.toLowerCase();
		bProp = bProp.toLowerCase();
		return aProp < bProp ? -1 : aProp > bProp ? 1 : 0;
	});
};

export function reorderList<T>(list: T[], fromIndex: number, toIndex: number) {
	return list.reduce((prev: T[], current: T, idx: number, self) => {
		if (fromIndex === toIndex) {
			prev.push(current);
		}
		if (idx === fromIndex) {
			return prev;
		}
		if (fromIndex < toIndex) {
			prev.push(current);
		}
		if (idx === toIndex) {
			const selfFromIndex = self[fromIndex];
			if (!selfFromIndex) return prev;
			prev.push(selfFromIndex);
		}
		if (fromIndex > toIndex) {
			prev.push(current);
		}
		return prev;
	}, []);
}

export const sortByProperty = <T, K extends string | number | Date>(arr: T[], key: keyof T | ((item: T) => K), desc = false): T[] =>
	[...arr].sort((a, b) => {
		const valA = typeof key === 'function' ? key(a) : (a[key] as K);
		const valB = typeof key === 'function' ? key(b) : (b[key] as K);
		const result = valA < valB ? -1 : valA > valB ? 1 : 0;
		return desc ? -result : result;
	});

export function arraysMatch(a: string[], b: string[]): boolean {
	if (a.length !== b.length) return false;

	const counts = new Map<string, number>();

	for (const x of a) {
		counts.set(x, (counts.get(x) ?? 0) + 1);
	}

	for (const y of b) {
		const count = counts.get(y);
		if (count === undefined) return false;
		if (count === 1) counts.delete(y);
		else counts.set(y, count - 1);
	}

	return counts.size === 0;
}
